import React, { useContext, useState } from 'react';
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { appConfiguration } from './appConfiguration.ts';
import { MetadataContext, MetaDataLoadingState } from './metadataProvider.tsx';
import { LoadingBanner, LoadingState } from './components/loadingBanner.tsx';
import { BeamlineDto, ProposalDto, EsafDto } from './api/api.ts';
import { SelectingStatus } from './components/inputAutocomplete.tsx';
import { CurrentUserContext } from './currentUserProvider.tsx';
import { BeamlineAutocomplete, BeamlineSearchFunctions } from './components/beamlineAutocomplete.tsx';
import { ProposalAutocomplete, ProposalSearchFunctions } from './components/proposalAutocomplete.tsx';
import { Coordinates, SortableTable } from './components/sortableTable.tsx';
import { timestampToTodayString, truthyJoin, sortWithNumberParsing } from './utils.tsx';
import './homePage.css';


// An elaboration of the different columns we can expect in the Esaf table.
enum EsafField {
  Name, Description, Proposal, Beamline, StartDate, Archived
};
const esafFieldDisplayOrder: EsafField[] = [
  EsafField.Name, EsafField.Description, EsafField.Proposal, EsafField.Beamline, EsafField.StartDate, EsafField.Archived
];
const columnNames = [
  "Name", "Description", "Proposal", "Beamline", "Start Date", "Archived"
];


const HomePage: React.FC = () => {

  const currentUser = useContext(CurrentUserContext);
  const metaData = useContext(MetadataContext);
  const esafs = metaData.esafs;
  const beamlines = metaData.beamlines;

  const [beamlineValue, setBeamlineValue] = useState<BeamlineDto | null>(null)
  const [proposalValue, setProposalValue] = useState<ProposalDto | null>(null)
  const [showArchived, setShowArchived] = useState<boolean>(false)

  const [tableSortColumn, setTableSortColumn] = useState<EsafField>(EsafField.StartDate);
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(true);

  const header = (
    <nav className="level">
      <div className="level-item has-text-centered">
        <img
          src={`${appConfiguration.config.baseUrl}/static/alt_spectrum_logo-dark.png`}
          alt="The ALS Logo"
          style={{height: "2.3em", marginRight: "1em"}}
        />
        <h1 className="title">ALS Sample Tracker</h1>
      </div>
    </nav>
  );

  // If we're waiting for metadata to come in, show a banner.
  if (metaData.loadingState != MetaDataLoadingState.Succeeded) {
    return (
      <div style={{padding: ".375rem 1rem"}}>
        { header }
        <p style={{marginBottom: "1.7em"}}>
          Welcome, {currentUser.name}! This application is for configuring and tracking samples at the ALS.
        </p>
        <LoadingBanner state={LoadingState.Loading} message={"Loading your information..."}></LoadingBanner>
      </div>
    );
  }


  function clickedTableSortHeader(f: EsafField) {
    const sortDirection = (f == tableSortColumn) ? !tableSortReverse : tableSortReverse;
    setTableSortColumn(f);
    setTableSortReverse(sortDirection);
  }


  const beamlineSearchFunctions:BeamlineSearchFunctions = {
    itemSelected: (item: BeamlineDto) => {
      setBeamlineValue(item);
      return SelectingStatus.Success
    },
    selectedNone: () => {
      setBeamlineValue(null);
      return SelectingStatus.Success
    }
  };

  const proposalSearchFunctions:ProposalSearchFunctions = {
    itemSelected: (item: ProposalDto) => {
      setProposalValue(item);
      return SelectingStatus.Success
    },
    selectedNone: () => {
      setProposalValue(null);
      return SelectingStatus.Success }
  };

  const beamlinesOwned = metaData.beamlines.slugsInDisplayOrder?.filter((slug) => {
    const beamline = metaData.beamlines.bySlug.get(slug)!;
    return beamline.ids_owners.includes(currentUser.userDetails?.id || -1);
  }).map((slug) => metaData.beamlines.bySlug.get(slug)!) || [];

  // If there are any archived ESAFs, we'll show a filtering checkbox to reveal them.
  const anyArchived = esafs.slugsInDisplayOrder?.some((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    return esaf.archived;
  });

  const filteredEsafs = esafs.slugsInDisplayOrder?.filter((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    if (proposalValue) {
      if (esaf.slug_proposal !== proposalValue.slug) {
        return false;
      }
    }
    if (beamlineValue) {
      if (esaf.slug_beamline !== beamlineValue.slug) {
        return false;
      }
    }
    if (!showArchived && esaf.archived) {
      return false;
    }
    return true;
  }) || [];

  // If any of the ESAFs that passed filtering are archived, we'll show an "Archived" column below.
  const anyArchivedFiltered = filteredEsafs.some((slug) => {
    const esaf = metaData.esafs.bySlug.get(slug)!;
    return esaf.archived;
  });

  function sortEsafs(esafSlugs: string[], field: EsafField, reverse: boolean): string[] {
    const esafs = esafSlugs.map((slug) => metaData.esafs.bySlug.get(slug)!);
    var sorted:EsafDto[] = [];
    switch (field) {
      case EsafField.Name:
        sorted = esafs.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        break;
      case EsafField.Description:
        sorted = esafs.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        break;
      case EsafField.Proposal:
        sorted = esafs.sort((a, b) => {
          const aProposal = metaData.proposals.bySlug.get(a.slug_proposal)?.name || "";
          const bProposal = metaData.proposals.bySlug.get(b.slug_proposal)?.name || "";
          return sortWithNumberParsing(aProposal, bProposal, reverse);
        });
        break;
      case EsafField.Beamline:
        sorted = esafs.sort((a, b) => {
          const aBeamline = beamlines.bySlug.get(a.slug_beamline)?.name || "";
          const bBeamline = beamlines.bySlug.get(b.slug_beamline)?.name || "";
          return sortWithNumberParsing(aBeamline, bBeamline, reverse);
        });
        break;
      case EsafField.StartDate:
        sorted = esafs.sort((a, b) => {
          const aDate = Date.parse(a.date_earliest_start || "") || 0;
          const bDate = Date.parse(b.date_earliest_start || "") || 0;
          return reverse ? bDate - aDate : aDate - bDate;
        });
        break;
      case EsafField.Archived:
        sorted = esafs.sort(
            (a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)}
          ).sort(
            (a, b) => { return a.archived === b.archived ? 0 : (reverse ? b.archived : a.archived) ? -1 : 1 }
          );
        break;
    }
    return sorted.map((s) => s.slug);
  }

  const sortedFilteredEsafs = sortEsafs(filteredEsafs, tableSortColumn, tableSortReverse);


  function renderHeaderCell(x: number): React.JSX.Element {
    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    const fieldType = esafFieldDisplayOrder[x];
    const fieldName = columnNames[x];

    return (<th key={fieldName}
            className={truthyJoin("sortable", (tableSortColumn == fieldType) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader(fieldType) }
            scope="col">{fieldName}</th>);
  }


  // Function provided to the SortableTable.  Given coordinates, renders an editable cell.
  // The editable cell is given the activationStatus and cellFunctions prepared by the EditableTable.
  function renderTableCell(coordinates: Coordinates): React.JSX.Element {
    const thisEsaf = esafs.bySlug.get(sortedFilteredEsafs[coordinates.y]);
    if (!thisEsaf) {
      console.error("Could not find ESAF for slug " + sortedFilteredEsafs[coordinates.y]);
      return (<></>);
    }

    // Set the value for the cell, making any conversions needed, and apply additional per-column settings.
    switch (esafFieldDisplayOrder[coordinates.x]) {
      case EsafField.Name:
        return (<span className="name"><Link to={ "esaf/" + thisEsaf.slug }>{ thisEsaf.name }</Link></span>);
      case EsafField.Description:
        return (<>{thisEsaf.description ?? ""}</>);
      case EsafField.Proposal:
        const proposalName = metaData.proposals.bySlug.get(thisEsaf.slug_proposal)?.name || "";
        return (<span className="proposal">{proposalName}</span>);
      case EsafField.Beamline:
        const beamlineName = beamlines.bySlug.get(thisEsaf.slug_beamline)?.name || "";
        return (<span className="beamline">{beamlineName}</span>);
      case EsafField.StartDate:
        const properDate = Date.parse(thisEsaf.date_earliest_start || "") || 0;
        return (<>{properDate ? timestampToTodayString(properDate / 1000) : ""}</>);
      case EsafField.Archived:
        if (thisEsaf.archived) {
          return (<span className={ truthyJoin( "icon", "is-right", "is-small") }><FontAwesomeIcon icon={faCheck} /></span>);
        } else {
          return (<></>);
        }
      default:
        return (<></>);
      }
  }

  const sortableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    renderTableCell: renderTableCell
  };


  return (
      <div style={{padding: ".375rem 1rem"}}>
        { header }
        <p style={{marginBottom: "1.7em"}}>
          Welcome, {currentUser.name}! This application is for configuring and tracking samples at the ALS.  To get started, select an ESAF below.
        </p>

        { // Only show the filters if there is more than one option in either.
          (metaData.beamlines.slugsInDisplayOrder.length > 1 || metaData.proposals.slugsInDisplayOrder.length > 1 || anyArchived) && (
            <nav className="level is-align-items-start" style={{height: "4em", marginBottom: "0.1em"}}>
              <div className="level-left is-align-items-start">
                { metaData.beamlines.slugsInDisplayOrder.length > 1 && (
                  <div className="level-item">
                    <BeamlineAutocomplete
                        selectedItem={beamlineValue}
                        searchFunctions={beamlineSearchFunctions}
                        placeholder="Filter by Beamline"
                        greenWhenValid={false}
                        canSelectNone={true}
                        showClearButton={true}
                      />
                 </div>
                )}
                { metaData.proposals.slugsInDisplayOrder.length > 1 && (
                  <div className="level-item">
                    <ProposalAutocomplete
                        selectedItem={proposalValue}
                        searchFunctions={proposalSearchFunctions}
                        placeholder="Filter by Proposal"
                        greenWhenValid={false}
                        canSelectNone={true}
                        showClearButton={true}
                      />
                  </div>
                )}
                { anyArchived && (
                  <div className="level-item">

                    <button className="button" style={{ borderColor: "rgba(0,0,0,0)" }}
                      onClick={() => setShowArchived(!showArchived)}
                    >
                      <span className="icon">
                        <FontAwesomeIcon icon={showArchived ? faCheckSquare : faSquare} />
                      </span>
                      <span>Show Archived</span>
                    </button>

                  </div>
                )}
              </div>

              <div className="level-right">
                <div className="level-item">
                </div>
                <div className="level-item">
                </div>
              </div>
            </nav>
        )}

        { esafs.slugsInDisplayOrder.length == 0 ? (
          <p>( You do not have access to any ESAFs yet. )</p>
        ) : (
            sortedFilteredEsafs.length == 0 ? (
            <p>( No ESAFs match the selected filters. )</p>
          ) : (
            <SortableTable
                extraClasses="esaftable"
                rowIdentifiers={ sortedFilteredEsafs }
                columnCount={ esafFieldDisplayOrder.length }
                displayRowSelectionBoxes={ false }
                hiddenColumns={ anyArchivedFiltered ? [] : [EsafField.Archived] }
                tableFunctions={ sortableTableFunctions }
              />
          )
        )}

        { beamlinesOwned.length > 0 && (
          <>
            <p style={{ marginTop: "2em" }}>As the owner of a Beamline, you can also configure Scan Types and Parameters for it.</p>
            <table className="beamlinetable">
              <thead>
                <tr key="headers">
                  <th key="name" scope="col">Name</th>
                  <th key="description" scope="col">Description</th>
                </tr>
              </thead>
              <tbody>
                {
                  beamlinesOwned.map((beamline) => {
                    return (
                      <tr key={beamline.slug}>
                          <th scope="row"><Link to={ "/beamline/" + beamline.slug }>{ beamline.name }</Link></th>
                          <td>{ beamline.description }</td>
                      </tr>);
                  })
                }
              </tbody>
            </table>
          </>
        )}

      </div>
  )
}

export default HomePage
